import React, { useState } from 'react';
import {
  previewExpiredClassPurge,
  runExpiredClassPurge,
  type ClassPurgePreview,
  type ClassPurgeResult,
} from '../../services/adminClassPurgeService';
import './AdminClassPurgeSection.css';

const CONFIRM_WORD = '삭제';

/** 관리자 전용: 보관 기간이 지난 학년도 클래스·자료 묶음 수동 정리(미리보기 → 확인 입력 → 실행). */
export default function AdminClassPurgeSection() {
  const [preview, setPreview] = useState<ClassPurgePreview | null>(null);
  const [result, setResult] = useState<ClassPurgeResult | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    setConfirmText('');
    try {
      setPreview(await previewExpiredClassPurge());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const runPurge = async () => {
    if (confirmText.trim() !== CONFIRM_WORD) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await runExpiredClassPurge());
      setPreview(null);
      setConfirmText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const nothing = preview != null && preview.classCount === 0 && preview.bundleCount === 0;
  const failed = result
    ? result.results.filter((r) => !r.ok).length + result.bundles.filter((r) => !r.ok).length
    : 0;

  return (
    <article className="cp-dashboard-block cp-admin-users-pass-block cp-admin-class-purge" aria-labelledby="cp-admin-class-purge-head">
      <h3 className="cp-dashboard-block__head" id="cp-admin-class-purge-head">
        <span className="cp-dashboard-block__head-title">보관 기간 만료 자료 정리</span>
      </h3>
      <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
        <p className="cp-admin-class-purge__desc">
          학년도가 끝나고 보관 기간(3개월)이 지난 클래스와 학년도 자료(일일기록·할 일·예산 등)를 영구 삭제합니다. 먼저 대상을 확인한 뒤 실행하세요.
        </p>
        <button type="button" className="cp-btn-secondary" onClick={() => void loadPreview()} disabled={busy}>
          {busy && !preview ? '확인 중…' : '삭제 대상 확인'}
        </button>

        {error ? (
          <p className="cp-admin-class-purge__error" role="alert">
            {error}
          </p>
        ) : null}

        {preview ? (
          <div className="cp-admin-class-purge__preview">
            {nothing ? (
              <p role="status">지금 삭제할 대상이 없습니다.</p>
            ) : (
              <>
                <p role="status">
                  클래스 <strong>{preview.classCount}</strong>개, 학년도 자료 묶음 <strong>{preview.bundleCount}</strong>개가 삭제됩니다.
                  {preview.classCount >= preview.limitPerRun || preview.bundleCount >= preview.limitPerRun
                    ? ` (한 번에 최대 ${preview.limitPerRun}개씩 처리하므로 남은 대상은 다시 실행하세요.)`
                    : ''}
                </p>
                <table className="cp-admin-class-purge__table">
                  <thead>
                    <tr>
                      <th>소유자</th>
                      <th>학년도</th>
                      <th>클래스</th>
                      <th>자료 묶음</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.owners.map((o) => (
                      <tr key={o.userId}>
                        <td>{o.email ?? o.userId}</td>
                        <td>{o.years.join(', ')}</td>
                        <td>{o.classes}</td>
                        <td>{o.bundles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <label className="cp-admin-class-purge__confirm">
                  되돌릴 수 없습니다. 실행하려면 <strong>{CONFIRM_WORD}</strong>를 입력하세요.
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    disabled={busy}
                    aria-label={`확인 문구 ${CONFIRM_WORD} 입력`}
                  />
                </label>
                <button
                  type="button"
                  className="cp-btn-danger"
                  onClick={() => void runPurge()}
                  disabled={busy || confirmText.trim() !== CONFIRM_WORD}
                >
                  {busy ? '삭제 중…' : '영구 삭제 실행'}
                </button>
              </>
            )}
          </div>
        ) : null}

        {result ? (
          <p className="cp-admin-class-purge__result" role="status">
            완료: 클래스 {result.classesDeleted}개, 학년도 자료 묶음 {result.bundlesDeleted}개를 삭제했습니다.
            {failed ? ` 실패 ${failed}건이 있습니다. 다시 확인해 주세요.` : ''}
          </p>
        ) : null}
      </div>
    </article>
  );
}
